import React, { useEffect, useState, useMemo } from "react";
import api from "../api/client";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Card, CardHeader, CardTitle, CardContent, CardDescription, CardFooter } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { AlertTriangle, Search, CheckCircle2, Plus, Pencil, Trash2, X } from "lucide-react";

const EMPTY_CENARIO = { titulo: "", localidade: "", etapas: [{ n: 1, acao: "", responsavel: "", equipamento: "" }] };

export default function CenariosPage() {
  const [cenarios, setCenarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = create; object = edit
  const [form, setForm] = useState(EMPTY_CENARIO);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/cenarios");
      setCenarios(r.data);
    } catch {
      toast.error("Falha ao carregar cenários");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!query) return cenarios;
    const q = query.toLowerCase();
    return cenarios.filter(
      (c) =>
        c.titulo.toLowerCase().includes(q) ||
        c.key.toLowerCase().includes(q) ||
        c.localidade.toLowerCase().includes(q) ||
        c.etapas.some((e) => e.acao.toLowerCase().includes(q))
    );
  }, [cenarios, query]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_CENARIO);
    setDialogOpen(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({
      titulo: c.titulo,
      localidade: c.localidade,
      etapas: c.etapas.length ? c.etapas.map((e) => ({ ...e })) : [{ n: 1, acao: "", responsavel: "", equipamento: "" }],
    });
    setDialogOpen(true);
  };

  const addEtapa = () => {
    setForm((f) => ({
      ...f,
      etapas: [...f.etapas, { n: f.etapas.length + 1, acao: "", responsavel: "", equipamento: "" }],
    }));
  };

  const removeEtapa = (idx) => {
    setForm((f) => ({
      ...f,
      etapas: f.etapas.filter((_, i) => i !== idx).map((e, i) => ({ ...e, n: i + 1 })),
    }));
  };

  const updateEtapa = (idx, field, value) => {
    setForm((f) => ({
      ...f,
      etapas: f.etapas.map((e, i) => (i === idx ? { ...e, [field]: value } : e)),
    }));
  };

  const save = async () => {
    if (!form.titulo.trim()) return toast.error("Título é obrigatório");
    const payload = {
      titulo: form.titulo.trim(),
      localidade: form.localidade.trim(),
      etapas: form.etapas
        .filter((e) => e.acao.trim())
        .map((e, i) => ({ n: i + 1, acao: e.acao.trim(), responsavel: e.responsavel.trim(), equipamento: e.equipamento.trim() })),
    };
    if (!payload.etapas.length) return toast.error("Ao menos uma etapa é obrigatória");
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/cenarios/${editing.key}`, payload);
        toast.success("Cenário atualizado");
      } else {
        await api.post("/cenarios", payload);
        toast.success("Cenário criado");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (c) => {
    if (c.sistema) return toast.error("Cenários do sistema não podem ser excluídos. Use 'Editar'.");
    if (!window.confirm(`Excluir o cenário "${c.titulo}"?`)) return;
    try {
      await api.delete(`/cenarios/${c.key}`);
      toast.success("Cenário excluído");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao excluir");
    }
  };

  return (
    <div className="space-y-6" data-testid="cenarios-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight flex items-center gap-3">
            <AlertTriangle className="w-7 h-7 text-amber-500" />
            Catálogo de Cenários de Emergência
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Base curada usada automaticamente na geração do PRE. Você pode editar cenários do sistema ou criar novos.
          </p>
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          <div className="relative flex-1 sm:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar por título, localidade, ação..."
              className="pl-9"
              data-testid="cenarios-search-input"
            />
          </div>
          <Button onClick={openCreate} data-testid="cenarios-new-btn" className="gap-2 shrink-0">
            <Plus className="w-4 h-4" /> Novo
          </Button>
        </div>
      </div>

      <div className="flex gap-3 text-sm flex-wrap">
        <Badge variant="secondary" data-testid="cenarios-count-badge">{cenarios.length} cenários</Badge>
        <Badge variant="outline" className="gap-1">
          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
          Conformidade NR-01 / NR-23 / NR-26 / ABNT NBR 14276:2020
        </Badge>
      </div>

      {loading ? (
        <div className="text-muted-foreground">Carregando cenários…</div>
      ) : filtered.length === 0 ? (
        <div className="text-muted-foreground">Nenhum cenário corresponde ao filtro.</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {filtered.map((c) => (
            <Card key={c.key} data-testid={`cenario-card-${c.key}`} className="border-border/60 flex flex-col">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="space-y-1 flex-1 min-w-0">
                    <CardTitle className="text-lg leading-tight">{c.titulo}</CardTitle>
                    <CardDescription className="text-xs flex items-center gap-2">
                      <span className="font-mono uppercase tracking-wide text-[10px] text-muted-foreground/70">{c.key}</span>
                      {c.sistema && <Badge variant="outline" className="text-[9px] h-4 px-1">SISTEMA</Badge>}
                    </CardDescription>
                  </div>
                  <Badge variant="outline" className="shrink-0">{c.etapas.length} etapas</Badge>
                </div>
                <p className="text-sm text-muted-foreground pt-2">
                  <span className="font-medium text-foreground">Localidade:</span> {c.localidade}
                </p>
              </CardHeader>
              <CardContent className="flex-1">
                <Accordion type="single" collapsible>
                  <AccordionItem value="etapas" className="border-0">
                    <AccordionTrigger className="text-sm hover:no-underline py-2">
                      Ver procedimento detalhado
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs border-collapse">
                          <thead>
                            <tr className="border-b border-border/60">
                              <th className="text-left py-2 px-2 font-medium w-10">#</th>
                              <th className="text-left py-2 px-2 font-medium">Ação</th>
                              <th className="text-left py-2 px-2 font-medium w-40">Responsável</th>
                              <th className="text-left py-2 px-2 font-medium w-40">Equipamento</th>
                            </tr>
                          </thead>
                          <tbody>
                            {c.etapas.map((e) => (
                              <tr key={e.n} className="border-b border-border/30 last:border-0">
                                <td className="py-2 px-2 align-top font-semibold text-muted-foreground">{e.n}</td>
                                <td className="py-2 px-2 align-top">{e.acao}</td>
                                <td className="py-2 px-2 align-top text-muted-foreground">{e.responsavel}</td>
                                <td className="py-2 px-2 align-top text-muted-foreground">{e.equipamento || "—"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </CardContent>
              <CardFooter className="flex gap-2 pt-0">
                <Button size="sm" variant="outline" className="gap-1" onClick={() => openEdit(c)} data-testid={`cenario-edit-${c.key}`}>
                  <Pencil className="w-3.5 h-3.5" /> Editar
                </Button>
                {!c.sistema && (
                  <Button size="sm" variant="ghost" className="gap-1 text-destructive hover:text-destructive" onClick={() => remove(c)} data-testid={`cenario-delete-${c.key}`}>
                    <Trash2 className="w-3.5 h-3.5" /> Excluir
                  </Button>
                )}
              </CardFooter>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="cenario-form-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? `Editar cenário: ${editing.titulo}` : "Novo cenário de emergência"}</DialogTitle>
            <DialogDescription>
              Defina o título, a localidade de aplicação e as etapas do procedimento de resposta.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Título *</Label>
              <Input
                value={form.titulo}
                onChange={(e) => setForm({ ...form, titulo: e.target.value })}
                placeholder="Ex: Vazamento de Cloro Líquido"
                data-testid="cenario-form-titulo"
              />
            </div>
            <div>
              <Label>Localidade de aplicação</Label>
              <Input
                value={form.localidade}
                onChange={(e) => setForm({ ...form, localidade: e.target.value })}
                placeholder="Ex: Sala de máquinas, área de tratamento"
                data-testid="cenario-form-localidade"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Etapas do procedimento</Label>
                <Button type="button" size="sm" variant="outline" onClick={addEtapa} className="gap-1" data-testid="cenario-form-add-etapa">
                  <Plus className="w-3.5 h-3.5" /> Adicionar etapa
                </Button>
              </div>
              <div className="space-y-2">
                {form.etapas.map((e, idx) => (
                  <div key={`etapa-${idx}-${e.n}`} className="grid grid-cols-12 gap-2 items-start p-2 rounded-md border border-border/60 bg-muted/20">
                    <div className="col-span-1 font-semibold text-muted-foreground pt-2 text-center">{idx + 1}</div>
                    <Textarea
                      className="col-span-5 min-h-[60px]"
                      value={e.acao}
                      onChange={(ev) => updateEtapa(idx, "acao", ev.target.value)}
                      placeholder="Descrição da ação..."
                      data-testid={`cenario-etapa-acao-${idx}`}
                    />
                    <Input
                      className="col-span-3"
                      value={e.responsavel}
                      onChange={(ev) => updateEtapa(idx, "responsavel", ev.target.value)}
                      placeholder="Responsável"
                      data-testid={`cenario-etapa-responsavel-${idx}`}
                    />
                    <Input
                      className="col-span-2"
                      value={e.equipamento}
                      onChange={(ev) => updateEtapa(idx, "equipamento", ev.target.value)}
                      placeholder="Equipamento (— se nenhum)"
                      data-testid={`cenario-etapa-equipamento-${idx}`}
                    />
                    <Button type="button" size="icon" variant="ghost" className="col-span-1" onClick={() => removeEtapa(idx)} disabled={form.etapas.length <= 1} data-testid={`cenario-etapa-remove-${idx}`}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving} data-testid="cenario-form-save">
              {saving ? "Salvando..." : editing ? "Salvar alterações" : "Criar cenário"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
